import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Typography,
  Alert,
  Progress,
  Space,
  Divider
} from 'antd';
import {
  DollarOutlined,
  CreditCardOutlined,
  BankOutlined,
  MobileOutlined,
  FileTextOutlined,
  EuroOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  EditOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  ClockCircleOutlined,
  WalletOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const VersementModal = ({
  open,
  onClose,
  onSubmit,
  vente,
  clientNom = '',
  clientPrenom = '',
  montantTotal,
  montantDejaVerse,
  montantRestant,
  numeroFacture
}) => {
  const [form] = Form.useForm();
  const [modePaiement, setModePaiement] = useState('Espèces');
  const [montantSaisi, setMontantSaisi] = useState(0);

  // Calculs en temps réel selon la formule: montantRestant = montantTotal - montantVerse
  const montantVerseActuel = montantDejaVerse || 0;
  const montantRestantActuel = montantRestant || Math.max(0, montantTotal - montantVerseActuel);
 
  // Calcul du nouveau versement
  const nouveauVersement = parseFloat(montantSaisi) || 0;
  const montantVerseTotalApres = montantVerseActuel + nouveauVersement;
  const nouveauRestant = Math.max(0, montantTotal - montantVerseTotalApres);
  const pourcentagePaiement = montantTotal > 0
    ? Math.round((montantVerseActuel / montantTotal) * 100)
    : 0;
  const pourcentageApres = montantTotal > 0
    ? Math.round((montantVerseTotalApres / montantTotal) * 100)
    : 0;

  const fullName = `${clientPrenom} ${clientNom}`.trim() || 'Client';

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
     
      // Validation supplémentaire
      if (values.montantVerse <= 0) {
        return;
      }
     
      if (values.montantVerse > montantRestantActuel) {
        return;
      }

      // Préparer les données pour l'API
      const paiementData = {
        venteId: vente?.id || vente?._id,
        clientId: vente?.clientId,
        clientName: fullName,
        montant: parseFloat(values.montantVerse),
        modePaiement: values.modePaiement,
        nomBanque: values.nomBanque || undefined,
        note: values.note || undefined,
        montantTotal: montantTotal,
        montantVerseAvant: montantVerseActuel,
        montantVerseApres: montantVerseTotalApres,
        montantRestantAvant: montantRestantActuel,
        montantRestantApres: nouveauRestant,
        datePaiement: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        statut: 'Enregistré'
      };

      await onSubmit(paiementData);
      handleClose();
    } catch (error) {
      console.error('❌ Erreur validation formulaire:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setMontantSaisi(0);
    setModePaiement('Espèces');
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            padding: '8px', 
            background: 'linear-gradient(to right, #3b82f6, #2563eb)',
            borderRadius: '8px'
          }}>
            <DollarOutlined style={{ color: 'white', fontSize: '20px' }} />
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827' }}>
              Enregistrer un Versement
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Gestion des paiements client
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={700}
      centered
      destroyOnClose
      modalRender={(modal) => (
        <div>
          {modal}
          <style>{`
            .versement-modal .ant-modal-content {
              border-radius: 16px;
              overflow: hidden;
            }
            .versement-modal .ant-modal-header {
              padding: 24px 24px 16px;
              border-bottom: 1px solid #e5e7eb;
            }
            .versement-modal .ant-modal-body {
              padding: 24px;
            }
            .modern-input-number .ant-input-number-input {
              height: 48px;
              font-size: 16px;
            }
            .modern-textarea .ant-input-textarea textarea {
              padding: 12px;
              font-size: 14px;
            }
            .modern-dropdown .ant-select-item {
              padding: 8px 12px;
            }
          `}</style>
        </div>
      )}
      className="versement-modal"
    >
      {/* En-tête informatif */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          background: 'linear-gradient(to right, #eff6ff, #dbeafe)',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #bfdbfe',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                padding: '8px',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>
                <UserOutlined style={{ color: '#2563eb', fontSize: '16px' }} />
              </div>
              <div>
                <Text style={{ fontSize: '14px', fontWeight: '500', color: '#4b5563' }}>
                  Client
                </Text>
                <Text style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#111827',
                  display: 'block'
                }}>
                  {fullName}
                </Text>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text style={{ fontSize: '14px', fontWeight: '500', color: '#4b5563' }}>
                N° Facture
              </Text>
              <Text style={{
                fontSize: '18px',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: '#1d4ed8',
                display: 'block'
              }}>
                {numeroFacture}
              </Text>
            </div>
          </div>
         
          {/* Montant de la vente */}
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            border: '1px solid #dbeafe'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShoppingCartOutlined style={{ color: '#3b82f6' }} />
                <Text style={{ fontWeight: '500', color: '#374151' }}>
                  Montant de la vente
                </Text>
              </div>
              <Text style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#2563eb'
              }}>
                {montantTotal.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'XOF'
                })}
              </Text>
            </div>
          </div>
        </div>

        {/* Timeline du paiement */}
        <div style={{
          background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ClockCircleOutlined style={{ color: '#4b5563' }} />
              <Text style={{ fontWeight: '500', color: '#374151' }}>
                Progression du paiement
              </Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                Date: {dayjs().format('DD/MM/YYYY')}
              </Text>
              <Text style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>
                Heure: {dayjs().format('HH:mm:ss')}
              </Text>
            </div>
          </div>
         
          {/* Progression actuelle */}
          <Progress
            percent={pourcentagePaiement}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
            strokeWidth={8}
            showInfo={false}
          />
         
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            fontSize: '14px',
            color: '#4b5563'
          }}>
            <span>
              Payé: {montantVerseActuel.toLocaleString('fr-FR', { 
                style: 'currency', 
                currency: 'XOF' 
              })}
            </span>
            <span>
              Reste: {montantRestantActuel.toLocaleString('fr-FR', { 
                style: 'currency', 
                currency: 'XOF' 
              })}
            </span>
          </div>
         
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            marginTop: '8px',
            textAlign: 'center',
            background: 'white',
            padding: '8px',
            borderRadius: '8px'
          }}>
            <span style={{ fontWeight: '500' }}>Formule appliquée:</span>{' '}
            Montant Restant = Montant Total ({montantTotal.toLocaleString('fr-FR', { 
              style: 'currency', 
              currency: 'XOF' 
            })}) - Montant Versé ({montantVerseActuel.toLocaleString('fr-FR', { 
              style: 'currency', 
              currency: 'XOF' 
            })})
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          modePaiement: 'Espèces'
        }}
        style={{ width: '100%' }}
      >
        {/* Section Mode de Paiement */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <div style={{ padding: '8px', background: '#eff6ff', borderRadius: '8px' }}>
              <CreditCardOutlined style={{ color: '#2563eb' }} />
            </div>
            <Text style={{ fontWeight: '600', color: '#1f2937', fontSize: '18px' }}>
              Mode de paiement
            </Text>
          </div>
         
          <Form.Item
            name="modePaiement"
            label={<span style={{ fontWeight: '500', color: '#374151' }}>Sélectionnez le mode de paiement</span>}
            rules={[{ required: true, message: 'Veuillez sélectionner le mode de paiement' }]}
          >
            <Select
              placeholder="Choisissez le mode de paiement"
              size="large"
              style={{ width: '100%' }}
              onChange={setModePaiement}
              dropdownClassName="modern-dropdown"
            >
              <Option value="Espèces">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                  <div style={{ padding: '8px', background: '#f0fdf4', borderRadius: '8px' }}>
                    <DollarOutlined style={{ color: '#16a34a' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>Espèces</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Paiement en liquide</div>
                  </div>
                </div>
              </Option>
              <Option value="Chèque">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                  <div style={{ padding: '8px', background: '#eff6ff', borderRadius: '8px' }}>
                    <FileTextOutlined style={{ color: '#2563eb' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>Chèque</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Paiement par chèque</div>
                  </div>
                </div>
              </Option>
              <Option value="Virement">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                  <div style={{ padding: '8px', background: '#faf5ff', borderRadius: '8px' }}>
                    <BankOutlined style={{ color: '#9333ea' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>Virement</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Virement bancaire</div>
                  </div>
                </div>
              </Option>
              <Option value="Carte">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                  <div style={{ padding: '8px', background: '#fff7ed', borderRadius: '8px' }}>
                    <CreditCardOutlined style={{ color: '#ea580c' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>Carte bancaire</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Paiement par carte</div>
                  </div>
                </div>
              </Option>
              <Option value="Mobile money">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                  <div style={{ padding: '8px', background: '#f0fdfa', borderRadius: '8px' }}>
                    <MobileOutlined style={{ color: '#0d9488' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500' }}>Mobile money</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Paiement mobile</div>
                  </div>
                </div>
              </Option>
            </Select>
          </Form.Item>

          {/* Nom de la banque (conditionnel) */}
          {(modePaiement === 'Virement' || modePaiement === 'Chèque') && (
            <Form.Item
              name="nomBanque"
              label={<span style={{ fontWeight: '500', color: '#374151' }}>Nom de la banque</span>}
              rules={[{ required: true, message: 'Veuillez saisir le nom de la banque' }]}
              style={{ marginTop: '16px' }}
            >
              <Input
                placeholder="Ex: BICIS, BOA, CBAO, etc."
                size="large"
                prefix={<BankOutlined style={{ color: '#9ca3af' }} />}
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}
        </div>

        {/* Section Montant */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <div style={{ padding: '8px', background: '#f0fdf4', borderRadius: '8px' }}>
              <EuroOutlined style={{ color: '#16a34a' }} />
            </div>
            <Text style={{ fontWeight: '600', color: '#1f2937', fontSize: '18px' }}>
              Montant du versement
            </Text>
          </div>

          <Form.Item
            name="montantVerse"
            label={<span style={{ fontWeight: '500', color: '#374151' }}>Montant à verser</span>}
            rules={[
              { required: true, message: 'Veuillez saisir le montant à verser' },
              {
                validator: (_, value) => {
                  if (!value || value <= 0) {
                    return Promise.reject(new Error('Le montant doit être supérieur à 0'));
                  }
                  if (value > montantRestantActuel) {
                    return Promise.reject(new Error(`Le montant ne peut pas dépasser ${montantRestantActuel.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}`));
                  }
                  return Promise.resolve();
                }
              }
            ]}
            help={
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Montant maximum autorisé: {montantRestantActuel.toLocaleString('fr-FR', { 
                  style: 'currency', 
                  currency: 'XOF' 
                })}
              </div>
            }
          >
            <InputNumber
              placeholder="Saisissez le montant"
              style={{ width: '100%' }}
              size="large"
              min={1}
              max={montantRestantActuel}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={value => value?.replace(/\s/g, '') || ''}
              prefix={<EuroOutlined style={{ color: '#9ca3af' }} />}
              className="modern-input-number"
              onChange={(value) => setMontantSaisi(value || 0)}
            />
          </Form.Item>

          {/* Calcul en temps réel selon la formule */}
          {montantSaisi > 0 && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: 'linear-gradient(to right, #fff7ed, #ffedd5)',
              borderRadius: '8px',
              border: '1px solid #fed7aa'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <CalculatorOutlined style={{ color: '#ea580c' }} />
                <Text strong style={{ color: '#1f2937' }}>
                  Calcul automatique selon la formule
                </Text>
              </div>
             
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: '#4b5563' }}>Montant total</Text>
                  <Text style={{ fontWeight: '600', color: '#1f2937' }}>
                    {montantTotal.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                  </Text>
                </div>
               
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: '#4b5563' }}>Déjà versé</Text>
                  <Text style={{ fontWeight: '600', color: '#16a34a' }}>
                    {montantVerseActuel.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                  </Text>
                </div>
               
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: '#4b5563' }}>Nouveau versement</Text>
                  <Text style={{ fontWeight: '600', color: '#2563eb' }}>
                    {montantSaisi.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                  </Text>
                </div>
               
                <Divider style={{ margin: '8px 0', borderColor: '#fed7aa' }} />
               
                <div style={{ paddingTop: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text style={{ color: '#374151' }}>Total versé après</Text>
                    <Text style={{ fontWeight: '600', color: '#9333ea' }}>
                      {montantVerseTotalApres.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                    </Text>
                  </div>
                 
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '8px',
                    borderTop: '1px solid #fed7aa'
                  }}>
                    <Text strong style={{ color: '#1f2937' }}>
                      Montant restant après versement
                    </Text>
                    <Text strong style={{ fontSize: '20px', color: '#c2410c' }}>
                      {nouveauRestant.toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' })}
                    </Text>
                  </div>
                 
                  {/* Progression après versement */}
                  <div style={{ marginTop: '12px' }}>
                    <Progress
                      percent={pourcentageApres}
                      strokeColor="#10b981"
                      size="small"
                      format={() => `${pourcentageApres}%`}
                    />
                  </div>
                 
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '8px',
                    padding: '8px',
                    background: 'white',
                    borderRadius: '8px'
                  }}>
                    <span style={{ fontWeight: '500' }}>Formule appliquée:</span>{' '}
                    Montant Restant = {montantTotal.toLocaleString('fr-FR')} - {montantVerseTotalApres.toLocaleString('fr-FR')} = {nouveauRestant.toLocaleString('fr-FR')} FCFA
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section Note additionnelle */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <div style={{ padding: '8px', background: '#faf5ff', borderRadius: '8px' }}>
              <EditOutlined style={{ color: '#9333ea' }} />
            </div>
            <Text style={{ fontWeight: '600', color: '#1f2937', fontSize: '18px' }}>
              Note additionnelle
            </Text>
          </div>

          <Form.Item
            name="note"
            label={<span style={{ fontWeight: '500', color: '#374151' }}>Informations complémentaires (optionnel)</span>}
          >
            <TextArea
              placeholder="Ajoutez des détails sur ce versement, comme une référence, un commentaire, etc."
              rows={3}
              size="large"
              maxLength={500}
              showCount
              className="modern-textarea"
              style={{ fontSize: '14px' }}
            />
          </Form.Item>
        </div>

        {/* Boutons d'action */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '16px',
          paddingTop: '24px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button
            onClick={handleClose}
            size="large"
            style={{
              paddingLeft: '32px',
              paddingRight: '32px',
              height: '48px',
              borderRadius: '12px',
              borderColor: '#d1d5db',
              color: '#374151'
            }}
          >
            <CloseOutlined style={{ marginRight: '8px' }} />
            Annuler
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            disabled={montantSaisi <= 0 || montantSaisi > montantRestantActuel}
            style={{
              paddingLeft: '32px',
              paddingRight: '32px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(to right, #2563eb, #1d4ed8)',
              border: '0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
              fontWeight: '600'
            }}
            icon={<CheckCircleOutlined />}
          >
            Enregistrer le versement
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default VersementModal;